import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Ticket, 
  Plus, 
  Eye, 
  Copy, 
  Trash2, 
  Lock, 
  Unlock,
  DollarSign,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  Shield,
  QrCode,
  Download,
  Share2,
  Key,
  CreditCard,
  AlertCircle,
  CheckSquare
} from 'lucide-react';
import { Ticket as TicketType, TicketClass, TicketStatus, TicketBalance } from '../../types/ticket.types';
import PinSetupModal from './PinSetupModal';
import PinValidationModal from './PinValidationModal';
import TicketDetailsModal from './TicketDetailsModal';

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  color: white;
  padding: 20px;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  color: #ffd700;
  display: flex;
  align-items: center;
  gap: 15px;
`;

const ActionButton = styled.button`
  background: linear-gradient(45deg, #4caf50, #8bc34a);
  border: none;
  color: white;
  padding: 12px 24px;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 8px 25px rgba(76, 175, 80, 0.3);
  }
`;

const BalanceSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const BalanceCard = styled.div<{ color: string }>`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 25px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.color};
  }
`;

const BalanceAmount = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #4caf50;
  margin-bottom: 10px;
`;

const BalanceLabel = styled.div`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

const Tab = styled.button<{ isActive: boolean }>`
  background: ${props => props.isActive ? 'rgba(255, 255, 255, 0.2)' : 'transparent'};
  border: none;
  color: white;
  padding: 15px 25px;
  border-radius: 15px 15px 0 0;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  border-bottom: 3px solid ${props => props.isActive ? '#ffd700' : 'transparent'};
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const TicketsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const TicketCard = styled.div<{ status: TicketStatus }>`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 25px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  }
  
  ${props => {
    switch (props.status) {
      case 'used':
        return `
          opacity: 0.6;
          &::after {
            content: 'USED';
            position: absolute;
            top: 20px;
            right: 20px;
            background: #f44336;
            color: white;
            padding: 5px 15px;
            border-radius: 15px;
            font-size: 0.8rem;
            font-weight: 600;
          }
        `;
      case 'expired':
        return `
          opacity: 0.5;
          &::after {
            content: 'EXPIRED';
            position: absolute;
            top: 20px;
            right: 20px;
            background: #ff9800;
            color: white;
            padding: 5px 15px;
            border-radius: 15px;
            font-size: 0.8rem;
            font-weight: 600;
          }
        `;
      case 'cancelled':
        return `
          opacity: 0.4;
          &::after {
            content: 'CANCELLED';
            position: absolute;
            top: 20px;
            right: 20px;
            background: #9e9e9e;
            color: white;
            padding: 5px 15px;
            border-radius: 15px;
            font-size: 0.8rem;
            font-weight: 600;
          }
        `;
      default:
        return '';
    }
  }}
`;

const TicketHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const TicketClassBadge = styled.div<{ class: TicketClass }>`
  background: ${props => {
    switch (props.class) {
      case 'first': return 'linear-gradient(45deg, #ffd700, #ffed4e)';
      case 'second': return 'linear-gradient(45deg, #c0c0c0, #e5e5e5)';
      case 'third': return 'linear-gradient(45deg, #cd7f32, #daa520)';
      case 'premium': return 'linear-gradient(45deg, #9c27b0, #e1bee7)';
      case 'vip': return 'linear-gradient(45deg, #ff5722, #ff8a65)';
      default: return 'linear-gradient(45deg, #2196f3, #64b5f6)';
    }
  }};
  color: ${props => props.class === 'second' ? '#333' : 'white'};
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
`;

const TicketAmount = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: #4caf50;
  margin-bottom: 15px;
`;

const TicketInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: 20px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
`;

const TicketActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const ActionIconButton = styled.button<{ variant: 'primary' | 'secondary' | 'danger' }>`
  background: ${props => {
    switch (props.variant) {
      case 'primary': return 'rgba(76, 175, 80, 0.2)';
      case 'secondary': return 'rgba(33, 150, 243, 0.2)';
      case 'danger': return 'rgba(244, 67, 54, 0.2)';
      default: return 'rgba(255, 255, 255, 0.1)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.variant) {
      case 'primary': return 'rgba(76, 175, 80, 0.5)';
      case 'secondary': return 'rgba(33, 150, 243, 0.5)';
      case 'danger': return 'rgba(244, 67, 54, 0.5)';
      default: return 'rgba(255, 255, 255, 0.3)';
    }
  }};
  color: ${props => {
    switch (props.variant) {
      case 'primary': return '#4caf50';
      case 'secondary': return '#2196f3';
      case 'danger': return '#f44336';
      default: return 'white';
    }
  }};
  padding: 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    transform: scale(1.1);
    background: ${props => {
      switch (props.variant) {
        case 'primary': return 'rgba(76, 175, 80, 0.3)';
        case 'secondary': return 'rgba(33, 150, 243, 0.3)';
        case 'danger': return 'rgba(244, 67, 54, 0.3)';
        default: return 'rgba(255, 255, 255, 0.2)';
      }
    }};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: rgba(255, 255, 255, 0.7);
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
  opacity: 0.5;
`;

const EmptyText = styled.div`
  font-size: 1.2rem;
  margin-bottom: 10px;
`;

const EmptySubtext = styled.div`
  font-size: 1rem;
  opacity: 0.8;
`;

const PinStatusIndicator = styled.div<{ hasPin: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
  border-radius: 15px;
  background: ${props => props.hasPin ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 152, 0, 0.2)'};
  border: 1px solid ${props => props.hasPin ? 'rgba(76, 175, 80, 0.5)' : 'rgba(255, 152, 0, 0.5)'};
  color: ${props => props.hasPin ? '#4caf50' : '#ff9800'};
  font-size: 0.8rem;
  font-weight: 600;
`;

// Mock data for demonstration
const mockTickets: TicketType[] = [
  {
    id: '1',
    userId: 'user1',
    ticketClass: 'first',
    amount: 1000,
    status: 'active',
    purchaseDate: new Date('2024-01-15'),
    expiryDate: new Date('2024-12-31'),
    ticketLink: 'https://lavernai.com/ticket/abc123',
    pin: '1234',
    isActive: true,
    metadata: {
      lavernaiName: 'LavernAI Competition Ticket',
      amountStaked: 1000,
      timestamp: '2024-01-15T10:30:00Z',
      validationHash: 'abc123def456',
      legalInfo: 'Valid for nationwide competitions',
      terms: 'Standard competition terms apply'
    }
  },
  {
    id: '2',
    userId: 'user1',
    ticketClass: 'second',
    amount: 500,
    status: 'used',
    purchaseDate: new Date('2024-01-10'),
    expiryDate: new Date('2024-12-31'),
    usedDate: new Date('2024-01-12'),
    competitionId: 'comp123',
    ticketLink: 'https://lavernai.com/ticket/def456',
    pin: '5678',
    isActive: false,
    metadata: {
      lavernaiName: 'LavernAI Competition Ticket',
      amountStaked: 500,
      timestamp: '2024-01-10T14:20:00Z',
      validationHash: 'def456ghi789',
      legalInfo: 'Valid for nationwide competitions',
      terms: 'Standard competition terms apply'
    }
  }
];

const mockBalance: TicketBalance = {
  availableBalance: 2500,
  totalTickets: 5,
  activeTickets: 3,
  usedTickets: 2,
  totalSpent: 3500
};

interface TicketManagementProps {
  userBalance?: number;
  onTicketPurchased?: (ticket: TicketType) => void;
  onNavigateToCompetitions?: () => void;
}

const TicketManagement: React.FC<TicketManagementProps> = ({ 
  userBalance = mockBalance.availableBalance,
  onTicketPurchased,
  onNavigateToCompetitions
}) => {
  const [activeTab, setActiveTab] = useState<'all' | 'active' | 'used' | 'expired'>('all');
  const [tickets, setTickets] = useState<TicketType[]>(mockTickets);
  const [balance, setBalance] = useState<TicketBalance>(mockBalance);
  const [selectedTicket, setSelectedTicket] = useState<TicketType | null>(null);
  const [showPinSetup, setShowPinSetup] = useState(false);
  const [showPinValidation, setShowPinValidation] = useState(false);
  const [showTicketDetails, setShowTicketDetails] = useState(false);
  const [purchaseConfirmation, setPurchaseConfirmation] = useState<{
    show: boolean;
    ticketClass: TicketClass | null;
    amount: number;
  }>({ show: false, ticketClass: null, amount: 0 });

  const filteredTickets = tickets.filter(ticket => {
    switch (activeTab) {
      case 'active': return ticket.status === 'active';
      case 'used': return ticket.status === 'used';
      case 'expired': return ticket.status === 'expired';
      default: return true;
    }
  });

  const handlePurchaseTicket = () => {
    // This will be handled by the parent component
    if (onNavigateToCompetitions) {
      onNavigateToCompetitions();
    }
  };

  const handleViewTicket = (ticket: TicketType) => {
    setSelectedTicket(ticket);
    setShowTicketDetails(true);
  };

  const handleCopyLink = async (ticket: TicketType) => {
    try {
      await navigator.clipboard.writeText(ticket.ticketLink);
      // Show success notification
      console.log('Ticket link copied to clipboard');
    } catch (err) {
      console.error('Failed to copy ticket link');
    }
  };

  const handleCancelTicket = (ticket: TicketType) => {
    if (ticket.status === 'active') {
      const updatedTickets = tickets.map(t => 
        t.id === ticket.id ? { ...t, status: 'cancelled' as TicketStatus } : t
      );
      setTickets(updatedTickets);
      
      // Update balance
      setBalance(prev => ({
        ...prev,
        activeTickets: prev.activeTickets - 1,
        availableBalance: prev.availableBalance + ticket.amount
      }));
    }
  };

  const handleSetPin = (ticket: TicketType) => {
    setSelectedTicket(ticket);
    setShowPinSetup(true);
  };

  const handlePinSetupComplete = (pin: string) => {
    if (selectedTicket) {
      const updatedTickets = tickets.map(t => 
        t.id === selectedTicket.id ? { ...t, pin } : t
      );
      setTickets(updatedTickets);
      setShowPinSetup(false);
      setSelectedTicket(null);
    }
  };

  const handlePinValidationComplete = (isValid: boolean) => {
    setShowPinValidation(false);
    if (isValid && selectedTicket) {
      // Allow ticket usage
      console.log('PIN validated, ticket can be used');
    }
  };

  const getStatusColor = (status: TicketStatus) => {
    switch (status) {
      case 'active': return '#4caf50';
      case 'used': return '#f44336';
      case 'expired': return '#ff9800';
      case 'cancelled': return '#9e9e9e';
      default: return '#2196f3';
    }
  };

  const getStatusIcon = (status: TicketStatus) => {
    switch (status) {
      case 'active': return <CheckCircle size={16} />;
      case 'used': return <XCircle size={16} />;
      case 'expired': return <Clock size={16} />;
      case 'cancelled': return <XCircle size={16} />;
      default: return <Info size={16} />;
    }
  };

  return (
    <Container>
      <Header>
        <Title>
          <Ticket size={40} />
          Ticket Management
        </Title>
        <ActionButton onClick={handlePurchaseTicket}>
          <Plus size={20} />
          Purchase Ticket
        </ActionButton>
      </Header>

      <BalanceSection>
        <BalanceCard color="#4caf50">
          <BalanceAmount>KES {balance.availableBalance.toLocaleString()}</BalanceAmount>
          <BalanceLabel>Available Balance</BalanceLabel>
        </BalanceCard>
        <BalanceCard color="#2196f3">
          <BalanceAmount>{balance.activeTickets}</BalanceAmount>
          <BalanceLabel>Active Tickets</BalanceLabel>
        </BalanceCard>
        <BalanceCard color="#ff9800">
          <BalanceAmount>{balance.totalTickets}</BalanceAmount>
          <BalanceLabel>Total Tickets</BalanceLabel>
        </BalanceCard>
        <BalanceCard color="#9c27b0">
          <BalanceAmount>KES {balance.totalSpent.toLocaleString()}</BalanceAmount>
          <BalanceLabel>Total Spent</BalanceLabel>
        </BalanceCard>
      </BalanceSection>

      <TabsContainer>
        <Tab 
          isActive={activeTab === 'all'} 
          onClick={() => setActiveTab('all')}
        >
          All Tickets ({tickets.length})
        </Tab>
        <Tab 
          isActive={activeTab === 'active'} 
          onClick={() => setActiveTab('active')}
        >
          Active ({tickets.filter(t => t.status === 'active').length})
        </Tab>
        <Tab 
          isActive={activeTab === 'used'} 
          onClick={() => setActiveTab('used')}
        >
          Used ({tickets.filter(t => t.status === 'used').length})
        </Tab>
        <Tab 
          isActive={activeTab === 'expired'} 
          onClick={() => setActiveTab('expired')}
        >
          Expired ({tickets.filter(t => t.status === 'expired').length})
        </Tab>
      </TabsContainer>

      {filteredTickets.length === 0 ? (
        <EmptyState>
          <EmptyIcon>🎫</EmptyIcon>
          <EmptyText>No tickets found</EmptyText>
          <EmptySubtext>
            {activeTab === 'all' 
              ? 'Purchase your first ticket to get started with competitions!'
              : `No ${activeTab} tickets available.`
            }
          </EmptySubtext>
        </EmptyState>
      ) : (
        <TicketsGrid>
          {filteredTickets.map((ticket) => (
            <TicketCard key={ticket.id} status={ticket.status}>
              <TicketHeader>
                <TicketClassBadge class={ticket.ticketClass}>
                  {ticket.ticketClass.toUpperCase()} CLASS
                </TicketClassBadge>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {getStatusIcon(ticket.status)}
                  <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                    {ticket.status.toUpperCase()}
                  </span>
                </div>
              </TicketHeader>

              <TicketAmount>KES {ticket.amount.toLocaleString()}</TicketAmount>

              <TicketInfo>
                <InfoItem>
                  <Calendar size={16} />
                  <span>Purchased: {ticket.purchaseDate.toLocaleDateString()}</span>
                </InfoItem>
                <InfoItem>
                  <Clock size={16} />
                  <span>Expires: {ticket.expiryDate.toLocaleDateString()}</span>
                </InfoItem>
                {ticket.usedDate && (
                  <InfoItem>
                    <CheckCircle size={16} />
                    <span>Used: {ticket.usedDate.toLocaleDateString()}</span>
                  </InfoItem>
                )}
                <InfoItem>
                  <PinStatusIndicator hasPin={!!ticket.pin}>
                    {ticket.pin ? <Lock size={16} /> : <Unlock size={16} />}
                    <span>{ticket.pin ? 'PIN Set' : 'No PIN'}</span>
                  </PinStatusIndicator>
                </InfoItem>
              </TicketInfo>

              <TicketActions>
                <ActionIconButton 
                  variant="secondary" 
                  onClick={() => handleViewTicket(ticket)}
                  title="View Details"
                >
                  <Eye size={16} />
                </ActionIconButton>
                
                {ticket.status === 'active' && (
                  <>
                    <ActionIconButton 
                      variant="primary" 
                      onClick={() => handleCopyLink(ticket)}
                      title="Copy Ticket Link"
                    >
                      <Copy size={16} />
                    </ActionIconButton>
                    
                    <ActionIconButton 
                      variant="secondary" 
                      onClick={() => handleSetPin(ticket)}
                      title={ticket.pin ? 'Change PIN' : 'Set PIN'}
                    >
                      {ticket.pin ? <Lock size={16} /> : <Unlock size={16} />}
                    </ActionIconButton>
                    
                    <ActionIconButton 
                      variant="danger" 
                      onClick={() => handleCancelTicket(ticket)}
                      title="Cancel Ticket"
                    >
                      <Trash2 size={16} />
                    </ActionIconButton>
                  </>
                )}
              </TicketActions>
            </TicketCard>
          ))}
        </TicketsGrid>
      )}

      {/* Modals */}
      {showPinSetup && selectedTicket && (
        <PinSetupModal
          ticket={selectedTicket}
          onClose={() => setShowPinSetup(false)}
          onComplete={handlePinSetupComplete}
        />
      )}

      {showPinValidation && selectedTicket && (
        <PinValidationModal
          ticket={selectedTicket}
          onClose={() => setShowPinValidation(false)}
          onComplete={handlePinValidationComplete}
        />
      )}

      {showTicketDetails && selectedTicket && (
        <TicketDetailsModal
          ticket={selectedTicket}
          onClose={() => setShowTicketDetails(false)}
          onCopyLink={() => handleCopyLink(selectedTicket)}
        />
      )}
    </Container>
  );
};

export default TicketManagement;
